/**
 * AudioBus — thin Web Audio GainNode chain for bus-level ducking and
 * pitch-ladder combo effects (W4) + Phaser sound wiring (W6 stub stage).
 *
 * Node graph:
 *
 *   AudioContext.destination
 *         ^
 *      masterGain  (volume 1.0)
 *       ^        ^
 *  musicDucker   sfxGain
 *  (GainNode)   (GainNode)
 *
 * musicDucker.gain is ramped down during duck() calls, then recovered.
 * sfxGain is a passthrough at 1.0; future W6 can add compressor here.
 *
 * All construction is deferred to first use and wrapped in try/catch
 * so environments without AudioContext (test harness, old browsers) get
 * a silent no-op instance rather than a crash.
 *
 * W6 stub stage (2026-05-29):
 *   playMusic / playSFX now optionally wire into a Phaser scene via
 *   attachScene(). When attached, the bus lazy-loads silence stub WAV
 *   files (public/assets/audio/{bgm,sfx}/<key>.wav) and triggers
 *   scene.sound.add(key).play(). Without a scene the methods keep the
 *   W4 console.debug behavior — headless tests stay safe.
 *
 *   The stub files are silence; boss can swap them for real CC0 audio
 *   (1 commit) once an asset plan is picked. Key -> filename map lives
 *   in AUDIO_ASSET_PATHS below and is the single source of truth shared
 *   with scripts/gen-audio-stub.cjs.
 */

/**
 * Asset key registry — 5 BGM + 20 SFX silence stub WAVs.
 *
 * MUST stay in sync with scripts/gen-audio-stub.cjs (BGM_KEYS + SFX_KEYS).
 * Key naming is the call-site contract; filename is replaceable. When
 * boss picks a CC0 plan, replace the WAVs (same filename) or swap to
 * .ogg by updating only this map's right-hand side.
 *
 * Path is relative to /public — Phaser loader resolves against the
 * Vite BASE_URL at runtime.
 */
export const AUDIO_ASSET_PATHS: Readonly<Record<string, string>> = Object.freeze({
  // BGM (3 sec silence loops)
  'home-ambient':     'assets/audio/bgm/home-ambient.wav',
  'academy-ambient':  'assets/audio/bgm/academy-ambient.wav',
  'hub-ambient':      'assets/audio/bgm/hub-ambient.wav',
  'battle-base':      'assets/audio/bgm/battle-base.wav',
  'result-victory':   'assets/audio/bgm/result-victory.wav',
  // SFX — UI (5)
  'button-click':     'assets/audio/sfx/button-click.wav',
  'answer-correct':   'assets/audio/sfx/answer-correct.wav',
  'answer-wrong':     'assets/audio/sfx/answer-wrong.wav',
  'box-upgrade':      'assets/audio/sfx/box-upgrade.wav',
  'level-up':         'assets/audio/sfx/level-up.wav',
  // SFX — Battle / boss (5)
  'boss-enter':       'assets/audio/sfx/boss-enter.wav',
  'boss-defeat':      'assets/audio/sfx/boss-defeat.wav',
  'player-hit':       'assets/audio/sfx/player-hit.wav',
  'enemy-explode':    'assets/audio/sfx/enemy-explode.wav',
  'skill-release':    'assets/audio/sfx/skill-release.wav',
  // SFX — Progression (5)
  'streak-up':        'assets/audio/sfx/streak-up.wav',
  'chapter-mastered': 'assets/audio/sfx/chapter-mastered.wav',
  'hover':            'assets/audio/sfx/hover.wav',
  'dismiss':          'assets/audio/sfx/dismiss.wav',
  'typing':           'assets/audio/sfx/typing.wav',
  // SFX — Misc UI (5)
  'notification':     'assets/audio/sfx/notification.wav',
  'scroll':           'assets/audio/sfx/scroll.wav',
  'swipe':            'assets/audio/sfx/swipe.wav',
  'select':           'assets/audio/sfx/select.wav',
  'cancel':           'assets/audio/sfx/cancel.wav',
});

/**
 * Minimal structural Phaser surface AudioBus needs. We avoid importing
 * Phaser to keep this file zero-dep — callers pass `this` (Phaser.Scene)
 * which structurally satisfies the interface.
 */
interface PhaserSoundInstance {
  play: () => void;
  stop?: () => void;
  destroy?: () => void;
}
interface PhaserSceneLike {
  sound?: {
    add: (key: string, config?: { loop?: boolean; volume?: number }) => PhaserSoundInstance;
  };
  load?: {
    audio: (key: string, urls: string | string[]) => unknown;
    once: (event: string, cb: () => void) => void;
    start: () => void;
  };
  cache?: {
    audio?: {
      exists: (key: string) => boolean;
    };
  };
}

export class AudioBus {
  /** Singleton AudioContext shared across the bus. */
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private musicDucker: GainNode | null = null;
  private sfxGain: GainNode | null = null;

  /** Combo state for pitchLadder. */
  private _comboCount: number = 0;
  private _comboResetTimer: ReturnType<typeof setTimeout> | null = null;

  private static readonly COMBO_MAX = 6;
  /** +6% per consecutive hit (Godot takeaway 5). */
  private static readonly COMBO_PITCH_STEP = 0.06;
  private static readonly COMBO_RESET_MS = 2000;

  /**
   * Optional Phaser scene used to load/play real audio assets. Null
   * keeps the W4 console.debug behavior (headless tests, pure-logic
   * paths). attachScene() wires it; detachScene() clears it.
   */
  private _scene: PhaserSceneLike | null = null;

  /**
   * Currently-playing BGM instance, kept so playMusic() with a new key
   * can stop the previous track instead of layering multiple loops.
   */
  private _currentMusic: PhaserSoundInstance | null = null;
  private _currentMusicKey: string | null = null;

  /**
   * Attach a Phaser scene so playMusic/playSFX wire to real audio.
   *
   * Call site contract: pass `this` from inside a Phaser scene's
   * create() (or later). The bus does NOT call scene.load.audio()
   * during preload — it lazy-loads on first play(), which keeps
   * scenes that never trigger an SFX from paying load cost.
   *
   * Safe to call multiple times; latest scene wins. Passing null
   * detaches and resumes console.debug mode (used by destroy()).
   */
  attachScene(scene: PhaserSceneLike | null): void {
    this._scene = scene;
  }

  /**
   * Lazily create the AudioContext + GainNode chain.
   * Idempotent — safe to call multiple times.
   * Returns false when AudioContext is unavailable (test / old browser).
   */
  private ensureChain(): boolean {
    if (this.ctx) return true;
    try {
      const Ctor =
        (globalThis as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
        (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return false;
      const ctx = new Ctor() as AudioContext;
      const master = ctx.createGain();
      master.gain.value = 1.0;
      master.connect(ctx.destination);
      const ducker = ctx.createGain();
      ducker.gain.value = 1.0;
      ducker.connect(master);
      const sfx = ctx.createGain();
      sfx.gain.value = 1.0;
      sfx.connect(master);
      this.ctx = ctx;
      this.masterGain = master;
      this.musicDucker = ducker;
      this.sfxGain = sfx;
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Ensure the asset for `key` is loaded into the Phaser sound cache,
   * then invoke `whenReady`. If the cache already has it (or the cache
   * surface is absent — e.g. test stub) we call `whenReady` immediately.
   *
   * Returns true iff the load was kicked off OR `whenReady` was invoked
   * synchronously; false iff scene is missing critical surfaces and we
   * could not proceed (caller should fall back to console.debug stub).
   */
  private _ensureLoaded(key: string, whenReady: () => void): boolean {
    const scene = this._scene;
    if (!scene || !scene.sound) return false;
    // If the cache reports the key, play immediately.
    const cached = scene.cache?.audio?.exists?.(key);
    if (cached) { whenReady(); return true; }
    // No load surface (e.g. mock without loader) → still try whenReady
    // synchronously; sound.add will either succeed or throw, and we
    // try/catch the call-site.
    if (!scene.load || typeof scene.load.audio !== 'function') {
      try { whenReady(); } catch { /* noop */ }
      return true;
    }
    const path = AUDIO_ASSET_PATHS[key];
    if (!path) {
      // Unknown key — log once and fall back to debug stub so a typo
      // doesn't 404 spam the Network tab.
      // eslint-disable-next-line no-console
      console.debug(`[AudioBus] unknown key (no asset path): ${key}`);
      return false;
    }
    try {
      scene.load.audio(key, path);
      scene.load.once('filecomplete-audio-' + key, () => {
        try { whenReady(); } catch { /* scene torn down mid-load */ }
      });
      scene.load.start();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Play a BGM track by key. If a Phaser scene is attached, lazy-loads
   * the silence stub WAV and starts looping via scene.sound. Without a
   * scene, retains the W4 console.debug stub (headless tests stay safe).
   *
   * Stopping any previous BGM is the bus's responsibility so a
   * scene-switch chain (home->hub->battle) doesn't pile up loops.
   *
   * @param key   Asset key (must exist in AUDIO_ASSET_PATHS for real wire).
   * @param loop  Whether to loop the track. Default true.
   */
  playMusic(key: string, loop: boolean = true): void {
    this.ensureChain();
    const scene = this._scene;
    if (!scene || !scene.sound) {
      // eslint-disable-next-line no-console
      console.debug(`[AudioBus] playMusic key=${key} loop=${loop} (stub: no scene)`);
      return;
    }
    // Stop previous BGM if a different key is requested.
    if (this._currentMusicKey && this._currentMusicKey !== key) {
      try { this._currentMusic?.stop?.(); } catch { /* noop */ }
      try { this._currentMusic?.destroy?.(); } catch { /* noop */ }
      this._currentMusic = null;
      this._currentMusicKey = null;
    } else if (this._currentMusicKey === key && this._currentMusic) {
      // Same key already playing — idempotent; do not restart.
      return;
    }
    const started = this._ensureLoaded(key, () => {
      try {
        const inst = scene.sound!.add(key, { loop, volume: 1.0 });
        inst.play();
        this._currentMusic = inst;
        this._currentMusicKey = key;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug(`[AudioBus] playMusic add/play failed key=${key}:`, (e as Error)?.message);
      }
    });
    if (!started) {
      // eslint-disable-next-line no-console
      console.debug(`[AudioBus] playMusic fallback (load not started) key=${key}`);
    }
  }

  /**
   * Play a one-shot SFX. With scene attached, lazy-loads the silence
   * stub and triggers scene.sound.add(key).play() with the given volume.
   * Without a scene, falls back to W4 console.debug.
   *
   * SFX instances are fire-and-forget — Phaser's sound manager handles
   * disposal once playback finishes. We don't track them per-bus.
   *
   * @param key     SFX asset key.
   * @param volume  Gain multiplier 0..1. Default 1.0.
   */
  playSFX(key: string, volume: number = 1.0): void {
    this.ensureChain();
    const scene = this._scene;
    if (!scene || !scene.sound) {
      // eslint-disable-next-line no-console
      console.debug(`[AudioBus] playSFX key=${key} volume=${volume.toFixed(3)} (stub: no scene)`);
      return;
    }
    const clampedVol = Math.max(0, Math.min(1, volume));
    const started = this._ensureLoaded(key, () => {
      try {
        const inst = scene.sound!.add(key, { volume: clampedVol });
        inst.play();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug(`[AudioBus] playSFX add/play failed key=${key}:`, (e as Error)?.message);
      }
    });
    if (!started) {
      // eslint-disable-next-line no-console
      console.debug(`[AudioBus] playSFX fallback (load not started) key=${key} volume=${clampedVol.toFixed(3)}`);
    }
  }

  /**
   * Duck the music bus by `amount` dB for `ms` milliseconds, then recover.
   * Uses AudioParam.linearRampToValueAtTime for click-free transitions.
   *
   * Trigger: correct-answer event OR skill release.
   * Default: -12 dB, 800 ms (Godot takeaway 4).
   *
   * @param amount  Negative dB value (e.g. -12). Default -12.
   * @param ms      Duck duration in milliseconds. Default 800.
   */
  duck(amount: number = -12, ms: number = 800): void {
    if (!this.ensureChain() || !this.musicDucker || !this.ctx) return;
    const gain = this.musicDucker.gain;
    // dB → linear: 10^(dB/20)
    const targetLinear = Math.pow(10, amount / 20);
    const now = this.ctx.currentTime;
    const recoverAt = now + ms / 1000;
    // Cancel any scheduled ramps first so a rapid second duck() doesn't
    // double-stack into a click artifact.
    gain.cancelScheduledValues(now);
    // Anchor at current value so the ramp starts from where we are, not 0.
    gain.setValueAtTime(gain.value, now);
    // 50ms fade down.
    gain.linearRampToValueAtTime(targetLinear, now + 0.05);
    // Hold at duck level until the last 200ms.
    gain.setValueAtTime(targetLinear, Math.max(now + 0.05, recoverAt - 0.2));
    // 200ms fade back to unity.
    gain.linearRampToValueAtTime(1.0, recoverAt);
  }

  /**
   * Increment combo counter and play the SFX key at a higher pitch.
   * Each consecutive call within COMBO_RESET_MS raises pitch by COMBO_PITCH_STEP.
   * After COMBO_MAX hits the pitch is capped. Timer resets on each call.
   * If 2 seconds pass with no call, combo resets to 0.
   *
   * Maps to Godot takeaway 5: setRate(1.0 + combo * 0.06).
   *
   * @param key         SFX key to play at pitched rate.
   * @param multiplier  Optional override multiplier (ignores combo state).
   *                    Pass undefined to use internal combo counter.
   */
  pitchLadder(key: string, multiplier?: number): void {
    let rate: number;
    if (multiplier === undefined) {
      this._comboCount = Math.min(this._comboCount + 1, AudioBus.COMBO_MAX);
      if (this._comboResetTimer !== null) {
        clearTimeout(this._comboResetTimer);
      }
      this._comboResetTimer = setTimeout(() => {
        this._comboCount = 0;
        this._comboResetTimer = null;
      }, AudioBus.COMBO_RESET_MS);
      rate = 1.0 + this._comboCount * AudioBus.COMBO_PITCH_STEP;
    } else {
      // Explicit multiplier bypasses combo state entirely (no counter mutation,
      // no timer reset). Useful for one-off scripted pitched cues.
      rate = multiplier;
    }
    // W4 stub: log the pitch rate; W6 will connect to real AudioBufferSourceNode
    // eslint-disable-next-line no-console
    console.debug(`[AudioBus] pitchLadder key=${key} rate=${rate.toFixed(3)}`);
    // TODO(W6+): create AudioBufferSourceNode, set .playbackRate.value = rate,
    // connect to sfxGain. (pitchLadder still log-only — Phaser sound manager
    // doesn't expose rate per-instance the way Web Audio does; needs deeper
    // integration than the silence-stub round.)
  }

  /**
   * Reset combo counter immediately (call on wave end / player death).
   */
  resetCombo(): void {
    this._comboCount = 0;
    if (this._comboResetTimer !== null) {
      clearTimeout(this._comboResetTimer);
      this._comboResetTimer = null;
    }
  }

  /**
   * Tear down the AudioContext + GainNode chain (call on scene shutdown).
   * Safe to call multiple times — second call is a no-op.
   */
  destroy(): void {
    // Cancel pending combo timer so we don't leak a setTimeout past disposal.
    if (this._comboResetTimer !== null) {
      clearTimeout(this._comboResetTimer);
      this._comboResetTimer = null;
    }
    this._comboCount = 0;
    // Stop any in-flight BGM so a scene-restart doesn't leak loops.
    try { this._currentMusic?.stop?.(); } catch { /* noop */ }
    try { this._currentMusic?.destroy?.(); } catch { /* noop */ }
    this._currentMusic = null;
    this._currentMusicKey = null;
    // Detach scene — calls landing after destroy() degrade to debug stub.
    this._scene = null;
    // Disconnect nodes in reverse-build order. Each disconnect is try-guarded
    // so a partially-built chain (e.g. ensureChain failed mid-way) still
    // tears down cleanly.
    try { this.sfxGain?.disconnect(); } catch { /* noop */ }
    try { this.musicDucker?.disconnect(); } catch { /* noop */ }
    try { this.masterGain?.disconnect(); } catch { /* noop */ }
    this.sfxGain = null;
    this.musicDucker = null;
    this.masterGain = null;
    if (this.ctx) {
      const ctx = this.ctx;
      this.ctx = null;
      try {
        // Some test stubs lack .close(); guard accordingly. Real browsers
        // return a Promise — we don't await it (destroy is sync).
        const maybePromise = (ctx as AudioContext & { close?: () => Promise<void> }).close?.();
        if (maybePromise && typeof maybePromise.catch === 'function') {
          maybePromise.catch(() => { /* noop */ });
        }
      } catch { /* noop */ }
    }
  }
}
